import { useEffect, useMemo, useRef, useState } from "react";

import { EXERCISES, LINK } from "./exercisesData";






// ---------- UI components ----------

function Pill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-1.5 rounded-lg text-sm font-medium transition-all border",
        active
          ? "bg-white text-gray-900 border-gray-300 shadow-sm"
          : "bg-white/60 text-gray-600 border-white/40 hover:bg-white/80",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono bg-white/70 border border-white/60 text-gray-800">
      {children}
    </span>
  );
}

function CodeCard({ children }) {
  return (
    <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-xl shadow-sm p-4">
      <pre className="font-mono text-base sm:text-lg md:text-xl text-gray-900 whitespace-pre-wrap leading-relaxed">
        {children}
      </pre>
    </div>
  );
}

function GlossaryPanel({ compact = false }) {
  const Term = ({ term, ipa, children, example }) => (
    <div className="bg-white/80 border border-white/50 rounded-xl p-5 space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <div className="font-bold text-gray-900">{term}</div>
        {ipa && <span className="text-xs font-mono text-gray-600">{ipa}</span>}
      </div>
      <div className="text-gray-800 leading-relaxed">{children}</div>
      {example && (
        <div className="mt-2">
          <div className="text-xs text-gray-500 mb-1">Example</div>
          <div className="font-mono text-base sm:text-lg text-gray-900 bg-white/70 border border-white/60 rounded-lg px-3 py-2">
            {example}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div className="text-center text-sm text-gray-600">
        Terms used in Teacher / Extra notes
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Term
          term="Foot / beat"
          children={
            <>
              A <b>beat</b> is a strong step (stress). A <b>foot</b> is one beat plus its following weak syllables.
              We write rhythm like <span className="font-mono font-bold">Xx</span>, <span className="font-mono font-bold">Xxx</span>.
            </>
          }
          example="XxXx  →  step-da step-da"
        />

        <Term
          term="Anacrusis (pickup)"
          children={
            <>
              One or more <b>weak syllables before the first strong beat</b>. Say them fast and fairly flat.
            </>
          }
          example={`hun${LINK}STAver  (hun = pickup)`}
        />

        <Term
          term="Schwa"
          ipa="[ə]"
          children={
            <>
              A reduced “uh”-like vowel in weak syllables. In fast speech it can become very short or disappear in some contexts.
            </>
          }
          example="(weak syllable reduction)"
        />

        <Term
          term="Chaining / linking"
          children={
            <>
              No pause between words — the end of one word flows into the next. Marked with{" "}
              <span className="font-mono font-bold">{LINK}</span>.
            </>
          }
          example={`KAri${LINK}SNAkker`}
        />

        <Term
          term="Epenthesis"
          children={
            <>
              Inserting an extra vowel (often schwa) to break up a consonant cluster (e.g. *e-snakker*).
              We avoid it by keeping the chain continuous.
            </>
          }
          example="*e-snakker → snakker"
        />

        <Term
          term="Assimilation"
          children={
            <>
              A sound changes to become more like a neighbor (common in fast speech). Example: /n/ → [ŋ] before /k/ or /g/.
            </>
          }
          example={`hun${LINK}kan  →  [huŋ kan] (optional)`}
        />

        <Term
          term="Hiatus"
          children={
            <>
              Two vowels meeting across a word boundary (vowel-to-vowel). Norwegian typically links them smoothly — avoid glottal stop.
            </>
          }
          example={`ikke${LINK}Anne`}
        />

        <Term
          term="Retroflex (dialect feature)"
          children={
            <>
              In many apical-/r/ dialects, /r/ + dental can merge into a retroflex sound (e.g. [ʈ], [ɳ], [ɭ]).
              Dialect-dependent.
            </>
          }
          example="r + t → [ʈ] (many dialects)"
        />
      </div>
    </div>
  );
}


function ExerciseSlide({ ex }) {

  const hasExtra = (ex.extraSpecific?.length ?? 0) > 0;
  const [mode, setMode] = useState("student"); // "student" | "extra" | "teacher" | "glossary"


  // SAFETY: avoid crash if teacher block missing
const focus = ex.teacher?.focus ?? [];
const actions = ex.teacher?.actions ?? [];
const articulation = ex.teacher?.articulation ?? [];
const extraExercises = ex.teacher?.extraExercises ?? [];


  // If current mode is "extra" but this slide has no extra, fall back.
  useEffect(() => {
    if (!hasExtra && mode === "extra") setMode("student");
  }, [hasExtra, mode]);

  return (
    <div className="max-w-5xl mx-auto w-full space-y-6">
      <div className="text-center space-y-3">
        <div className="text-gray-600 text-sm">Rhythm patterns</div>
        <div className="flex flex-wrap justify-center gap-2">
          {(ex.patterns || []).map((p) => (
            <Chip key={p}>{p}</Chip>
          ))}
        </div>
       <div className="text-gray-500 text-sm">
  Examples: {(ex.examples ?? []).join(" · ")}
</div>

      </div>

      <div className="flex justify-center gap-2">
  <Pill active={mode === "student"} onClick={() => setMode("student")}>
    Student
  </Pill>

  {hasExtra && (
    <Pill active={mode === "extra"} onClick={() => setMode("extra")}>
      Extra
    </Pill>
  )}

  <Pill active={mode === "teacher"} onClick={() => setMode("teacher")}>
    Teacher
  </Pill>

  <Pill active={mode === "glossary"} onClick={() => setMode("glossary")}>
    Glossary
  </Pill>
</div>


      {mode === "student" && (
  <div className="space-y-4">
    <div className="text-center text-gray-700">Say it as one wave — no pauses.</div>
    <div className="space-y-3">
      {(ex.studentCore ?? []).map((line, idx) => (
        <CodeCard key={idx}>{line}</CodeCard>
      ))}
    </div>
  </div>
)}


      {mode === "extra" && hasExtra && (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-xl p-5">
            <div className="font-bold text-gray-900 mb-2">Optional helpers (unique for this exercise)</div>
            <ul className="list-disc pl-5 text-gray-700 space-y-2">
              {(ex.extraSpecific ?? []).map((t, i) => (
  <li key={i}>{t}</li>
))}

            </ul>
          </div>
          <div className="text-sm text-gray-500 text-center">Extra is optional — rhythm comes first.</div>
        </div>
      )}

      {mode === "teacher" && (
  <div className="space-y-4">
    {focus.length > 0 && (
      <div className="bg-white/80 border border-white/50 rounded-xl p-5">
        <div className="text-sm text-gray-500 mb-2">Teacher focus</div>
        <ul className="list-disc pl-5 text-gray-900 space-y-2">
          {focus.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </div>
    )}

    {articulation.length > 0 && (
      <div className="bg-white/80 border border-white/50 rounded-xl p-5">
        <div className="text-sm text-gray-500 mb-2">Articulation notes</div>
        <ul className="list-disc pl-5 text-gray-900 space-y-2">
          {articulation.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </div>
    )}

    {actions.length > 0 && (
      <div className="bg-white/80 border border-white/50 rounded-xl p-5">
        <div className="text-sm text-gray-500 mb-2">In-class actions</div>
        <ul className="list-disc pl-5 text-gray-900 space-y-2">
          {actions.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
        <div className="mt-3 text-xs text-gray-500">
          Tip: correct pauses first (flow), then stress placement, then small sound details.
        </div>
      </div>
    )}

    {extraExercises.length > 0 && (
      <div className="bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-xl p-5">
        <div className="font-bold text-gray-900 mb-2">Extra exercises</div>
        <ul className="list-disc pl-5 text-gray-700 space-y-2">
          {extraExercises.map((t, i) => (
            <li key={i}>{t}</li>
          ))}
        </ul>
      </div>
    )}

    {focus.length === 0 && actions.length === 0 && articulation.length === 0 && extraExercises.length === 0 && (
      <div className="bg-white/70 border border-white/50 rounded-xl p-5 text-center text-sm text-gray-600">
        No teacher notes for this exercise yet.
      </div>
    )}
  </div>
)}

{mode === "glossary" && (
  <div className="space-y-4">
    <div className="bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-xl p-5">
      <div className="font-bold text-gray-900 mb-2">Glossary</div>
      <div className="text-sm text-gray-600">
        Quick definitions you can reference while doing this exercise.
      </div>
    </div>

    <GlossaryPanel compact />
  </div>
)}





    </div>
  );
}

// ---------- Presentation ----------

export default function FlytOgRytmePresentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const slideDuration = 5000;

  const slides = useMemo(() => {
    const titleSlide = {
      title: "",
      content: (
        <div className="text-center space-y-6 py-10">
          <h1 className="text-5xl font-bold text-gray-900 font-mono">
            Flytende norsk<br />— Rhythm Slides
          </h1>
          <div className="text-gray-600">Use ← → / Space • P to play/pause</div>
        </div>
      ),
      bgColor: "bg-gradient-to-br from-blue-50 via-white to-purple-50",
    };

    const rhythmHelpSlide = {
      title: "How to read XxXx",
      content: (
        <div className="max-w-4xl mx-auto space-y-6 text-center">
          <div className="text-6xl">👣</div>

          <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-6 shadow-sm space-y-3">
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">Speech has steps (beats)</div>
            <div className="text-gray-700 text-base sm:text-lg">
              <span className="font-mono font-bold">X</span> = strong step (stress) •{" "}
              <span className="font-mono font-bold">x</span> = small step (unstressed)
            </div>
            <div className="text-gray-700 text-base sm:text-lg">
              Think: <span className="font-mono font-bold">step-da step-da</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-2xl p-6 shadow-sm">
              <div className="text-sm text-gray-500 mb-2">Example rhythm</div>
              <div className="font-mono text-3xl font-bold text-gray-900 mb-2">XxXx</div>
              <div className="text-lg text-gray-800">step-da step-da</div>
              <div className="text-sm text-gray-600 mt-2">
                Only the “step” parts are strong. The “da” parts are quick.
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-200 rounded-2xl p-6 shadow-sm">
              <div className="text-sm text-gray-500 mb-2">How we write sentences</div>
              <div className="bg-white/80 border border-white/60 rounded-xl p-4 space-y-3">
                <div className="font-mono text-2xl text-gray-900">KAri{LINK}SNAkker</div>
                <div className="font-mono text-2xl text-gray-900">hun{LINK}kan{LINK}RYDde</div>
              </div>
              <div className="text-sm text-gray-600 mt-3 space-y-1">
                <div>
                  <span className="font-mono">CAPS</span> = strong beat
                </div>
                <div>
                  <span className="font-mono">{LINK}</span> = connect words (no pause)
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200 rounded-2xl p-6 shadow-sm">
            <div className="text-xl font-bold text-gray-900 mb-2">One rule</div>
            <div className="text-gray-700">Keep going like a train 🚆 — don’t stop between words.</div>
          </div>
        </div>
      ),
      bgColor: "bg-gradient-to-br from-white to-blue-50",
    };

    const exerciseSlides = EXERCISES.map((ex) => ({
      title: `Exercise ${ex.id}`,
      content: <ExerciseSlide ex={ex} />,
      bgColor: "bg-gradient-to-br from-white to-blue-50",
    }));

    const legendSlide = {
      title: "Legend",
      content: (
        <div className="max-w-3xl mx-auto space-y-4">
          <div className="bg-white/80 border border-white/50 rounded-xl p-5">
            <div className="font-bold text-gray-900 mb-2">Visual coding</div>
            <ul className="list-disc pl-5 text-gray-800 space-y-2">
              <li>X = strong beat, x = weak beat</li>
              <li>CAPS show the strong beat in the sentence</li>
              <li>{LINK} = connect / no pause</li>
              <li>Extra tab appears only when there is something unique for that exercise</li>
            </ul>
          </div>
        </div>
      ),
      bgColor: "bg-gradient-to-br from-white to-gray-50",
    };

    return [titleSlide, rhythmHelpSlide, ...exerciseSlides, legendSlide];
  }, []);

  const playSlideshow = () => {
    if (isPlaying) {
      clearInterval(intervalRef.current);
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setCurrentSlide((cur) => {
        if (cur < slides.length - 1) return cur + 1;
        clearInterval(intervalRef.current);
        setIsPlaying(false);
        return cur;
      });
    }, slideDuration);
  };

  const nextSlide = () => setCurrentSlide((s) => Math.min(s + 1, slides.length - 1));
  const prevSlide = () => setCurrentSlide((s) => Math.max(s - 1, 0));
  const goToSlide = (i) => setCurrentSlide(Math.max(0, Math.min(i, slides.length - 1)));

  useEffect(() => {
    const handleKeyDown = (event) => {
      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          prevSlide();
          break;
        case "ArrowRight":
        case " ":
          event.preventDefault();
          nextSlide();
          break;
        case "p":
        case "P":
          event.preventDefault();
          playSlideshow();
          break;
        case "Home":
          event.preventDefault();
          goToSlide(0);
          break;
        case "End":
          event.preventDefault();
          goToSlide(slides.length - 1);
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides.length, isPlaying]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-3 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div className="text-xs sm:text-sm text-gray-500 font-medium">
            {currentSlide + 1} / {slides.length}
          </div>
          <div className="flex items-center space-x-3 w-full sm:w-auto">
            {isPlaying && (
              <div className="flex items-center text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></div>
                <span className="text-xs sm:text-sm">Playing</span>
              </div>
            )}
            <button
              onClick={playSlideshow}
              className={`px-4 py-2 rounded-lg font-medium transition-all text-xs sm:text-sm flex-1 sm:flex-none ${
                isPlaying
                  ? "bg-gradient-to-r from-red-500 to-orange-500 text-white"
                  : "bg-gradient-to-r from-blue-500 to-purple-500 text-white"
              }`}
            >
              {isPlaying ? "⏸️ Pause" : "▶️ Play"}
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="h-1.5 bg-gray-200 rounded-full mb-4 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
            style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
          />
        </div>

        {/* Slide */}
        <div className={`rounded-2xl shadow-xl overflow-hidden ${slides[currentSlide].bgColor} relative`}>
          <div className="p-4 sm:p-6 md:p-8 lg:p-12 min-h-[70vh] flex flex-col">
            {slides[currentSlide].title && (
              <div className="mb-6 text-center">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 font-mono">
                  {slides[currentSlide].title}
                </h2>
                <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mt-2" />
              </div>
            )}

            <div className="flex-grow flex items-center overflow-y-auto">
              <div className="w-full">{slides[currentSlide].content}</div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-6 flex justify-between items-center">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`px-4 py-2 rounded-lg text-xs sm:text-sm ${
              currentSlide === 0 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            ← Previous
          </button>

          <div className="flex space-x-1">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`w-2 h-2 rounded-full ${
                  currentSlide === i ? (isPlaying ? "bg-green-500 animate-pulse" : "bg-blue-500") : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`px-4 py-2 rounded-lg text-xs sm:text-sm ${
              currentSlide === slides.length - 1 ? "text-gray-400 cursor-not-allowed" : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            {currentSlide === slides.length - 1 ? "✓ Finish" : "Next →"}
          </button>
        </div>

        {/* Keyboard hint */}
        <div className="mt-4 text-center text-xs text-gray-400">
          ← → navigate • Space advance • P play/pause • Home/End jump
        </div>
      </div>
    </div>
  );
}
